<template>
  <div class="chat-panel-wrapper">
    <div class="chat-messages-area pt-4 px-4" ref="scrollableList"
      :style="data.maxHeight ? { maxHeight: data.maxHeight } : {}"
      @scroll="onScroll">
        <Loader v-if="loading" />
        <v-list v-else>
          <template v-for="(message) in messages">
            <v-list-item v-if="message.user?.id != user?.id" :key="message.createdAt">
              <v-list-item-avatar class="align-self-start mr-2">
                <v-avatar size="40">
                  <v-img :src="message.user?.profileImage"></v-img>
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content class="received-message">
                <v-card color="success" class="flex-none no-shadow">
                  <v-card-text class="white--text pa-2 d-flex flex-column no-shadow">
                    <span class="text-caption">{{ message.user.firstName }} </span>
                    <span class="align-self-start text-subtitle-1">{{ message.text }}</span>
                    <span class="text-caption font-italic align-self-end">{{ message.createdAtDisplay }}</span>
                    <ChatMedia v-if="message?.medias?.length > 0" :data="{ medias: message.medias }"></ChatMedia>
                  </v-card-text>
                </v-card>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-else :key="message.createdAt">
              <v-list-item-content class="sent-message justify-end">
                <v-card color="primary" class="flex-none no-shadow">
                  <v-card-text class="white--text pa-2 d-flex flex-column no-shadow">
                    <span class="text-subtitle-1 chat-message">{{ message.text }}</span>
                    <span class="text-caption font-italic align-self-start">{{ message.createdAtDisplay }}</span>
                    <ChatMedia v-if="message?.medias?.length > 0" :data="{ medias: message.medias }"></ChatMedia>
                  </v-card-text>
                </v-card>
              </v-list-item-content>
              <v-list-item-avatar class="align-self-start ml-2">
                <v-img :src="user.profilePicUrl"></v-img>
              </v-list-item-avatar>
            </v-list-item>
          </template>
        </v-list>
        <div ref="bottomElement"></div>
    </div>

    <div class="messages-form p-4" v-if="!data.isReadOnly">
      <v-textarea v-model="message" :disabled="isFileUploading || loading" v-on:keyup.enter="sendMessage(null)"
        :label="'Enter your message here to reply'" rows="3" max-rows="5" no-resize></v-textarea>

      <v-btn width="100px" :disabled="!message?.length || isMessageInvalid"
        :loading="isFileUploading || isSending || loading" class="btn-default inline-block float-right"
        @click="sendMessage(null)">
        {{ $t('GeneralTranslations.Send') }}
      </v-btn>

      <div class="float-left">
        <ChatMediaUploader :filePath="filePath" @onFileStartUpload="onFileStartUpload" @onFileUploaded="onFileUploaded"
          @onImageSelected="onImageSelected"></ChatMediaUploader>
      </div>
    </div>
  </div>
</template>

<script>
import * as firebase from "firebase/app";
import { mapGetters } from "vuex";
import { CommentSenderRoleEnum } from '@/enums/CommentSenderRoleEnum';
import { v4 as uuidv4 } from "uuid";
import { nextTick } from 'vue';
import { localizedDate } from "@/utils/localizedDate";
import ChatMedia from '@/components/chat/ChatMedia.vue'
import ChatMediaUploader from '@/components/chat/ChatMediaUploader.vue'
import Loader from '@/components/loader/Loader.vue';
import moment from "moment";
import { DEFAULT_AVATAR_WHITE_200x200 } from '@/consts/default-avatar';

export default {
  name: "Chat",
  components: { ChatMedia, ChatMediaUploader, Loader },
  props: {
    data: Object
  },
  data() {
    return {
      loading: true,
      message: null,
      messages: [],
      onMessagesUnsubscribe: null,
      isChatActive: false,
      isFileUploading: false,
      isSending: false,
      isAtBottom: true
    }
  },

  computed: {
    facility() {
      return this.$store.state.authModule.currentFacilityData
    },
    filePath() {
      return `${this.facility.id}/channel_chat_images/${this.data.familyMember.id}/${this.user.id}`;
    },
    isMessageInvalid() {
      return (!this.message || this.message.trim() === "") && !this.isUploadingFile && !this.media;
    },
    ...mapGetters({
      user: "user"
    })
  },

  async created() {
    await this.initChat()
  },

  updated() {
    if (this.isAtBottom) {
      this.scrollToLastItem();
    }
  },

 mounted() {
    const observer = new IntersectionObserver(([entry]) => {
      this.isAtBottom = entry.isIntersecting;
    }, { threshold: 1 });

    if (this.$refs.bottomElement) {
      observer.observe(this.$refs.bottomElement);
    }
  },

  beforeDestroy() {
    this.unsubscribeFromMessages()
    this.closeChat();
  },

  methods: {
    async initChat() {
      console.log('initChat()')
      this.messages = (await this.$store.dispatch('getFamilyMessages', { familyId: this.$route.params.id, duration: null }))
      .filter(x => x.channel === this.data.channel)

      if (!this.data.isReadOnly) await this.readIncomingMessages(this.data.familyMember.id)
      
      await this.subscribeOnMessages()
      await nextTick()
      this.scrollToLastItem()
      this.loading = false
    },

    closeChat() {
      console.log('closeChat')
      this.loading = false
      this.message = null
      this.messages = []
      this.isChatActive = false
      this.isFileUploading = false
    },

    async sendMessage(media = null) {

      if (this.isMessageInvalid && !media) return;

      this.isSending = true
      const newMessage = {
        id: uuidv4(),
        to: CommentSenderRoleEnum.Family,
        from: CommentSenderRoleEnum.Staff,
        channel: this.data.channel,
        createdAt: new Date().toISOString(),
        staffId: this.user.id,
        familyHubUserId: this.data.familyMember.id,
        read: false,
        readDateTime: null,
        text: this.message,
        facilityId: this.facility.id,
        orgId: this.facility.orgId,
        medias: [],
        user: {
          id: this.user.id,
          firstName: this.user.name,
          lastName: null,
          profileImage: this.user?.thumbnailUrl || DEFAULT_AVATAR_WHITE_200x200,
          customProperties: null
        }
      }

      if (media) {
        newMessage.medias = [media]
      }

      this.message = ''

      await this.$store.dispatch("createMessage", newMessage);
      this.scrollToLastItem()
      this.isSending = false
    },

    onFileStartUpload() {
      this.isFileUploading = true;
      console.log('onFileStartUpload');
    },

    onScroll() {
      const el = this.$refs.scrollableList;
      if (!el) return;
      this.isAtBottom = (el.scrollTop + el.clientHeight >= el.scrollHeight - 10);
    },

    async onFileUploaded(file) {
      if (!file?.url) {
        throw Error('Unable to upload file')
      }

      const media = {
        type: file.type,
        url: file.url
      }

      await this.sendMessage(media)
      this.isFileUploading = false
    },

    async onImageSelected(image) {

      if (!image?.image) {
        throw Error('Unable to select image')
      }

      const media = {
        type: 'image',
        url: image.image
      }

      await this.sendMessage(media)
    },

    async readIncomingMessages() {
      if (this.data.isReadOnly) return

      const result = await this.$store.dispatch("readIncomingFamilyMessages", this.data.channel)
      if (result === true && this.messages) {
        this.messages.forEach(item => {
          if (item.senderRole !== CommentSenderRoleEnum.Staff) {
            item.read = true
          }
        });
      }
    },

    async readIncomingMessage(message) {
      if (message.to !== CommentSenderRoleEnum.Staff) return

      const result = await this.$store.dispatch("readIncomingFamilyMessage", message.id)
      if (result === true) {
        const item = this.messages.find(x => x.id === message.id);
        item.read = true
      }
    },

    async subscribeOnMessages() {
      console.log(`subscribeOnMessages() channel: ${this.data.channel} facilityId: ${this.facility.id}`)

      const messagesQuery = firebase.firestore().collection("messages")
        .where('channel', '==', this.data.channel)
        .where("facilityId", "==", this.facility.id)
        // request data that was created after start listening
        .where("createdAt", ">=", new Date().toISOString())
        .orderBy("createdAt", "desc")

      this.onMessagesUnsubscribe = await messagesQuery
        .onSnapshot((snapshot) => {

          snapshot.docChanges().forEach(async (change) => {

            if (change.type === "added") {
              console.log(`received new message`)
              const data = change.doc.data()
              const message = { 
                  ...data,
                  id: change.doc.id, 
                  createdAtDisplay: moment(data.createdAt).isSame(moment(), 'day') ? localizedDate(data.createdAt, this.facility.timeFormat) : localizedDate(data.createdAt),
              }
              this.messages.push(message)
              this.$store.dispatch('pushMessageTofamilyMessagesStore', data);

              this.scrollToLastItem()
              this.readIncomingMessage(message)

              await this.$store.dispatch('updateLastMessageInFamilyStaffChat', { channel: this.data.channel, message: message })
            }

            if (change.type === "removed") {
              console.log(`interlocutor removed a message`)
              this.messages = this.messages.filter(x => x.id !== change.doc.id && x.id !== change.doc.data().id)
            }
          })
        })
    },

    unsubscribeFromMessages() {
      console.log('unsubscribeFromMessages')
      if (this.onMessagesUnsubscribe) {
        this.onMessagesUnsubscribe()
      }
    },

    scrollToLastItem() {
      this.$nextTick(() => {
        setTimeout(() => {
          const bottomElement = this.$refs.bottomElement;
          if (bottomElement) {
            bottomElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
        }, 500);
      });
    }
  }
}
</script>

<style scoped lang="scss">
.v-card {
  padding: initial !important;
  min-height: initial !important;
  background-color: #fff !important;
  box-shadow: none !important;
}

.chat-panel-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: 100%;
}

.chat-messages-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.messages-form {
  flex-shrink: 0;
  width: 100%;
}

::v-deep .messages-form .v-textarea textarea {
  min-height: 72px !important;
  resize: none;
}

.no-shadow {
  box-shadow: none !important;
}

.chat-message {
  display: unset !important;
  white-space: break-spaces;
}

.chat-screen {
  max-height: 320px;
  overflow-y: auto;
}

.flex-none {
  flex: unset;
}

.received-message,
.sent-message {
  word-break: break-word;
  cursor: text;
}

.received-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: 54px;
  right: auto;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #4caf50 transparent transparent transparent;
}

.sent-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: auto;
  right: 54px;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #3f51b5 transparent transparent transparent;
}

.chat-content {
  list-style: none;
  padding: 0;
  color: #fff;
}

.v-list-scrollable {
  overflow-y: auto !important;
  flex-grow: 1;
}

.v-list-scrollable::-webkit-scrollbar {
  width: 15px;
}

::v-deep .v-card { 
  box-shadow: none!important;
}

::v-deep .v-tabs__wrapper {
  box-shadow: none !important;
}

::v-deep .v-slide-group__wrapper {
  box-shadow: none !important;
}

</style>